import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, FC, KeyboardEvent, PointerEvent, ReactNode } from "react";
import { Button, Drawer, Grid, Layout, Menu, theme } from "antd";
import type { MenuProps } from "antd";
import { LeftOutlined, LogoutOutlined, RightOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useIsExistAuthentication, useLink, useLogout, useMenu, useWarnAboutChange } from "@refinedev/core";
import type { TreeMenuItem } from "@refinedev/core";
import { useThemedLayoutContext } from "@refinedev/antd";
import { AdminTitle } from "./AdminTitle";
import {
  SIDER_COLLAPSED_WIDTH,
  SIDER_DEFAULT_WIDTH,
  SIDER_MAX_WIDTH,
  SIDER_MIN_WIDTH,
  clampSiderWidth,
  readSiderOpenKeys,
  readSiderWidth,
  writeSiderOpenKeys,
  writeSiderWidth,
} from "./siderStorage";

const TITLE_HEIGHT = 64;
const KEYBOARD_STEP = 16;
const LOGOUT_KEY = "__logout";

type AdminSiderProps = {
  Title?: FC<{ collapsed: boolean }>;
};

type MenuItem = NonNullable<MenuProps["items"]>[number];

/**
 * Panel boczny w miejsce `ThemedSider` z Refine: zakładki pogrupowane (zasoby z
 * `meta.parent`), szerokość zmieniana przeciąganiem prawej krawędzi i zapamiętywana,
 * zwijanie do ikon jak w oryginale. Stan zwinięcia trzyma kontekst `ThemedLayout`.
 */
export const AdminSider: FC<AdminSiderProps> = ({ Title }) => {
  const { token } = theme.useToken();
  const { siderCollapsed, setSiderCollapsed, mobileSiderOpen, setMobileSiderOpen } = useThemedLayoutContext();
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu();
  const breakpoint = Grid.useBreakpoint();
  const Link = useLink();
  const isExistAuthentication = useIsExistAuthentication();
  const { mutate: logout } = useLogout();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();

  const RenderTitle = Title ?? AdminTitle;
  const isMobile = typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;

  // ── szerokość i przeciąganie ──
  const siderRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(readSiderWidth);
  const [dragging, setDragging] = useState(false);
  const [handleHover, setHandleHover] = useState(false);

  useEffect(() => {
    if (!dragging) writeSiderWidth(width);
  }, [dragging, width]);

  useEffect(() => {
    if (!dragging) return;
    const { userSelect, cursor } = document.body.style;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    return () => {
      document.body.style.userSelect = userSelect;
      document.body.style.cursor = cursor;
    };
  }, [dragging]);

  const onHandlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };
  const onHandlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const left = siderRef.current?.getBoundingClientRect().left ?? 0;
    setWidth(clampSiderWidth(event.clientX - left));
  };
  const onHandlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    setDragging(false);
  };
  const onHandleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta = event.key === "ArrowLeft" ? -KEYBOARD_STEP : event.key === "ArrowRight" ? KEYBOARD_STEP : 0;
    if (!delta) return;
    event.preventDefault();
    setWidth((current) => clampSiderWidth(current + delta));
  };

  // ── rozwinięte grupy ──
  const groupKeys = useMemo(
    () => menuItems.filter((item) => item.children.length > 0).map((item) => item.key),
    [menuItems],
  );
  // Pierwsza wizyta: wszystkie grupy otwarte. Potem zapamiętany stan plus grupa bieżącej
  // strony, żeby wejście z linku nie lądowało w zwiniętej sekcji.
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const stored = readSiderOpenKeys();
    return stored ? Array.from(new Set([...stored, ...defaultOpenKeys])) : groupKeys;
  });
  const onOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
    writeSiderOpenKeys(keys);
  };

  // ── wylogowanie (jak w ThemedSider: pyta, gdy są niezapisane zmiany) ──
  const handleLogout = () => {
    if (warnWhen) {
      if (!window.confirm("Masz niezapisane zmiany. Na pewno chcesz się wylogować?")) return;
      setWarnWhen(false);
    }
    logout();
  };

  const items = useMemo<MenuItem[]>(() => {
    const toItems = (tree: TreeMenuItem[]): MenuItem[] =>
      tree.map((item) => {
        const label = item.label ?? item.name;
        const icon: ReactNode = item.icon ?? <UnorderedListOutlined />;
        if (item.children.length > 0) {
          return { key: item.key, icon, label, children: toItems(item.children) };
        }
        return { key: item.key, icon, label: <Link to={item.route ?? ""}>{label}</Link>, title: label };
      });
    const tree = toItems(menuItems);
    if (!isExistAuthentication) return tree;
    return [...tree, { type: "divider" }, { key: LOGOUT_KEY, icon: <LogoutOutlined />, label: "Wyloguj" }];
  }, [Link, isExistAuthentication, menuItems]);

  const renderMenu = (collapsed: boolean) => (
    <Menu
      mode="inline"
      items={items}
      selectedKeys={selectedKey ? [selectedKey] : []}
      // Zwinięty panel pokazuje grupy jako wysuwane podmenu — wtedy antd sam zarządza
      // otwarciem; kontrolowane `openKeys` zablokowałoby wysuwanie i nadpisało zapamiętany stan.
      {...(collapsed ? {} : { openKeys, onOpenChange })}
      onClick={({ key }) => {
        if (key === LOGOUT_KEY) handleLogout();
        setMobileSiderOpen(false);
      }}
      style={{ border: "none", paddingTop: 8, height: `calc(100% - ${TITLE_HEIGHT}px)`, overflowY: "auto", overflowX: "hidden" }}
    />
  );

  const titleBar = (collapsed: boolean, barWidth: number | string) => (
    <div
      style={{
        width: barWidth,
        height: TITLE_HEIGHT,
        padding: collapsed ? 0 : "0 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        backgroundColor: token.colorBgElevated,
        overflow: "hidden",
      }}
    >
      <RenderTitle collapsed={collapsed} />
    </div>
  );

  if (isMobile) {
    return (
      <>
        <Drawer
          open={mobileSiderOpen}
          onClose={() => setMobileSiderOpen(false)}
          placement="left"
          closable={false}
          size={280}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ height: "100vh", backgroundColor: token.colorBgContainer }}>
            {titleBar(false, "100%")}
            {renderMenu(false)}
          </div>
        </Drawer>
        <Button
          size="large"
          icon={<UnorderedListOutlined />}
          aria-label="Otwórz menu"
          onClick={() => setMobileSiderOpen(true)}
          style={{ position: "fixed", top: 64, zIndex: 999, borderStartStartRadius: 0, borderEndStartRadius: 0 }}
        />
      </>
    );
  }

  const handleActive = dragging || handleHover;
  const siderStyle: CSSProperties = {
    position: "sticky",
    top: 0,
    height: "100vh",
    zIndex: 10,
    backgroundColor: token.colorBgContainer,
    borderRight: `1px solid ${token.colorBorderSecondary}`,
    // Bez animacji szerokości w trakcie przeciągania — inaczej panel „goni” kursor.
    ...(dragging ? { transition: "none" } : {}),
  };

  return (
    <Layout.Sider
      ref={siderRef}
      width={width}
      collapsible
      collapsed={siderCollapsed}
      collapsedWidth={SIDER_COLLAPSED_WIDTH}
      onCollapse={(collapsed, type) => {
        if (type === "clickTrigger") setSiderCollapsed(collapsed);
      }}
      style={siderStyle}
      trigger={
        <Button
          type="text"
          aria-label={siderCollapsed ? "Rozwiń panel" : "Zwiń panel"}
          style={{ borderRadius: 0, height: "100%", width: "100%", backgroundColor: token.colorBgElevated }}
        >
          {siderCollapsed ? (
            <RightOutlined style={{ color: token.colorPrimary }} />
          ) : (
            <LeftOutlined style={{ color: token.colorPrimary }} />
          )}
        </Button>
      }
    >
      {titleBar(siderCollapsed, "100%")}
      {renderMenu(siderCollapsed)}
      {!siderCollapsed && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Szerokość panelu bocznego"
          aria-valuemin={SIDER_MIN_WIDTH}
          aria-valuemax={SIDER_MAX_WIDTH}
          aria-valuenow={width}
          tabIndex={0}
          title="Przeciągnij, aby zmienić szerokość (dwuklik przywraca domyślną)"
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
          onPointerEnter={() => setHandleHover(true)}
          onPointerLeave={() => setHandleHover(false)}
          onFocus={() => setHandleHover(true)}
          onBlur={() => setHandleHover(false)}
          onDoubleClick={() => setWidth(SIDER_DEFAULT_WIDTH)}
          onKeyDown={onHandleKeyDown}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 6,
            height: "100%",
            cursor: "col-resize",
            zIndex: 2,
            touchAction: "none",
            outline: "none",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              right: 0,
              width: 2,
              backgroundColor: handleActive ? token.colorPrimary : "transparent",
              transition: "background-color 0.15s",
            }}
          />
        </div>
      )}
    </Layout.Sider>
  );
};
